import { clients, HttpError } from 'lib/https';
import { ConfigurationSettings } from 'models/configuration-settings';
import type {
  CouponPreviewRequest,
  CouponQuote,
  CreateTopUpRequest,
  CreditAccess,
  CreateTopUpResponse,
  CreditSpend,
  CreditSummary,
  Invoice,
  LedgerEntry,
  Page,
  SavedCard,
  TopUp,
} from '../types';

type CreditsPath =
  | 'creditsSummary'
  | 'creditsTopUps'
  | 'creditsLedger'
  | 'creditsSpend'
  | 'creditsAccess'
  | 'creditsAccessGet'
  | 'creditsCouponPreview'
  | 'creditsInvoices';

const creditsUrl = (config: ConfigurationSettings, path: CreditsPath) =>
  `${config.paths.seliseAiBaseUrl}${config.paths.seliseAi[path]}`;

const cardsUrl = (config: ConfigurationSettings) =>
  `https://${config.paths.baseUrl}${config.paths.businessService.paymentMethods}`;

const withQuery = (url: string, params: Record<string, string | number>) =>
  `${url}?${new URLSearchParams(
    Object.entries(params).map(([key, value]) => [key, String(value)])
  ).toString()}`;

/** The stable `code` the credits server puts on a refusal, wherever its error body keeps it. */
export const creditsErrorCode = (error: unknown): string => {
  const body = (error as { error?: { code?: unknown; detail?: unknown } })?.error;
  const detail = body?.detail as { code?: unknown } | string | undefined;
  const code = body?.code ?? (typeof detail === 'object' ? detail?.code : undefined);
  return typeof code === 'string' ? code : '';
};

const planRequestUrl = (config: ConfigurationSettings) =>
  `${config.paths.klaxServiceBaseUrl.replace(/\/$/, '')}${config.paths.planRequest}`;

/** Asks the organization's admins, through Klax's business service, to move the caller to another plan. */
export const requestPlanChange = (limitName: string, config: ConfigurationSettings) =>
  clients.post<{ sent: number }>(
    planRequestUrl(config),
    config.blocksKey,
    JSON.stringify({ limitName })
  );

/** Whether the caller already asked and is still on the plan they asked from, so no admin has acted yet. */
export const getPlanRequestStatus = (config: ConfigurationSettings) =>
  clients.get<{ pending: boolean }>(planRequestUrl(config), config.blocksKey);

export const getCreditSummary = (config: ConfigurationSettings) =>
  clients.get<CreditSummary>(creditsUrl(config, 'creditsSummary'), config.blocksKey);

export const createTopUp = (request: CreateTopUpRequest, config: ConfigurationSettings) =>
  clients.post<CreateTopUpResponse>(
    creditsUrl(config, 'creditsTopUps'),
    config.blocksKey,
    JSON.stringify(request)
  );

export const previewCoupon = (request: CouponPreviewRequest, config: ConfigurationSettings) =>
  clients.post<CouponQuote>(
    creditsUrl(config, 'creditsCouponPreview'),
    config.blocksKey,
    JSON.stringify(request)
  );

export const getTopUp = (topUpId: string, config: ConfigurationSettings) =>
  clients.get<TopUp>(`${creditsUrl(config, 'creditsTopUps')}/${topUpId}`, config.blocksKey);

export const cancelTopUp = (topUpId: string, config: ConfigurationSettings) =>
  clients.post<TopUp>(
    `${creditsUrl(config, 'creditsTopUps')}/${topUpId}/cancel`,
    config.blocksKey,
    '{}'
  );

/** The top-up a payment belongs to, or null when the payment is not a top-up (HTTP 404). */
export const findTopUpByPayment = async (
  paymentDetailId: string,
  config: ConfigurationSettings
): Promise<TopUp | null> => {
  try {
    return await clients.get<TopUp>(
      withQuery(creditsUrl(config, 'creditsTopUps'), { paymentDetailId }),
      config.blocksKey
    );
  } catch (error) {
    if (error instanceof HttpError && error.status === 404) return null;
    throw error;
  }
};

export const getTopUps = (page: number, pageSize: number, config: ConfigurationSettings) =>
  clients.get<Page<TopUp>>(
    withQuery(creditsUrl(config, 'creditsTopUps'), { page, pageSize }),
    config.blocksKey
  );

export const getLedger = (page: number, pageSize: number, config: ConfigurationSettings) =>
  clients.get<Page<LedgerEntry>>(
    withQuery(creditsUrl(config, 'creditsLedger'), { page, pageSize }),
    config.blocksKey
  );

/** With a `userId`, every figure, all time included, is that one person's. */
export const getCreditSpend = (config: ConfigurationSettings, userId?: string) => {
  const url = creditsUrl(config, 'creditsSpend');
  return clients.get<CreditSpend>(userId ? withQuery(url, { userId }) : url, config.blocksKey);
};

/** Which of `userIds` an admin switched off. The server leaves out everyone else, who is on. */
export const getCreditAccess = (userIds: string[], config: ConfigurationSettings) =>
  clients.post<CreditAccess>(
    creditsUrl(config, 'creditsAccessGet'),
    config.blocksKey,
    JSON.stringify({ userIds })
  );

export const setCreditAccess = (userId: string, allowed: boolean, config: ConfigurationSettings) =>
  clients.put<CreditAccess>(
    `${creditsUrl(config, 'creditsAccess')}/${encodeURIComponent(userId)}`,
    config.blocksKey,
    JSON.stringify({ allowed })
  );

export const getInvoices = (config: ConfigurationSettings) =>
  clients.get<{ items: Invoice[] }>(creditsUrl(config, 'creditsInvoices'), config.blocksKey);

/** The PDF is fetched with the auth header, so a plain link cannot be used. */
export const downloadInvoicePdf = async (invoice: Invoice, config: ConfigurationSettings) => {
  const response = await clients.get<Response>(
    `${creditsUrl(config, 'creditsInvoices')}/${invoice.id}/pdf`,
    config.blocksKey
  );
  const link = document.createElement('a');
  link.href = URL.createObjectURL(await response.blob());
  link.download = `${invoice.number}.pdf`;
  link.click();
  URL.revokeObjectURL(link.href);
};

export const getSavedCards = async (config: ConfigurationSettings): Promise<SavedCard[]> => {
  const response = await clients.get<{ data?: SavedCard[] | null }>(
    cardsUrl(config),
    config.blocksKey
  );
  return response?.data ?? [];
};
